import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calculator, ArrowRight, Sparkles, ExternalLink } from "lucide-react";
import { API } from "@/App";

const VERDICT_COLOR = {
  "Strong Draw": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Likely Draw": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Likely Home": "bg-blue-100 text-blue-700 border-blue-200",
  "Likely Away": "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Risky/Avoid": "bg-amber-100 text-amber-700 border-amber-300",
  "Risky":       "bg-amber-100 text-amber-700 border-amber-300",
  "Unknown":     "bg-gray-100 text-gray-500 border-gray-200",
};

const PoolAnalyzerWidget = () => {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const analyze = async () => {
    setError("");
    setResult(null);
    // Pre-validate: strip any digits > 49
    const raw = input.split(/[^0-9]+/).filter(Boolean).map(Number);
    const cleaned = [...new Set(raw.filter(n => n >= 1 && n <= 49))];
    if (cleaned.length < 2) {
      setError("Enter at least 2 numbers between 1 and 49.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai-pool-analyzer`, { numbers: cleaned });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't analyze right now. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <section className="px-4 py-10" data-testid="pool-analyzer-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6">
          <Badge className="bg-violet-600 text-white mb-2 inline-flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> AI POOL ANALYZER</Badge>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Confirm Your Pool Games</h2>
          <p className="text-sm text-gray-500 mt-1">Enter your pool numbers (e.g. 5, 12, 23, 45) — we&apos;ll analyze them using our predictive model and tell you how strong your slip is.</p>
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Enter Number (1-49)</label>
              <div className="flex gap-2 flex-col sm:flex-row">
                <Input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") analyze(); }}
                  placeholder="e.g. 5, 12, 23, 45 or 5 12 23 45"
                  className="text-base font-mono"
                  data-testid="pool-analyzer-input"
                />
                <Button
                  onClick={analyze}
                  disabled={loading}
                  className="bg-violet-600 hover:bg-violet-700 text-white px-6"
                  data-testid="pool-analyzer-btn"
                >
                  {loading ? "Analyzing…" : (<><Calculator className="h-4 w-4 mr-2" /> Analyze</>)}
                </Button>
              </div>
              {error && <p className="text-xs text-red-600" data-testid="pool-analyzer-error">{error}</p>}
              <p className="text-[11px] text-gray-400">Tip: <strong>3 numbers</strong> are analyzed as a Nap. <strong>4+ numbers</strong> are analyzed as a Perm 3. Numbers above 49 are removed automatically.</p>
            </div>

            {result && (
              <div className="border-t border-gray-100 pt-4 space-y-4" data-testid="pool-analyzer-result">
                {/* Headline */}
                <div className="flex items-center justify-between flex-wrap gap-2 bg-violet-50 border border-violet-200 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-xs text-violet-600 font-bold uppercase">{result.stake_type}</p>
                    <p className="text-lg font-extrabold text-gray-900">Slip Strength: {result.success_pct}%</p>
                    <p className="text-xs text-gray-600">Expected draws: {result.expected_draws} of {result.items.filter(i => i.found).length} picked</p>
                  </div>
                  <Badge className={`text-xs px-3 py-1 ${result.success_pct >= 60 ? "bg-emerald-600" : result.success_pct >= 40 ? "bg-amber-500" : "bg-rose-500"} text-white`}>
                    {result.success_pct >= 60 ? "STRONG" : result.success_pct >= 40 ? "DECENT" : "RISKY"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-700 italic" data-testid="pool-analyzer-verdict">{result.verdict_text}</p>

                {/* Per-pick rows */}
                <div className="space-y-1.5">
                  {result.items.map((it, i) => (
                    <div key={i} className={`flex items-center justify-between text-sm border rounded-md px-3 py-2 ${VERDICT_COLOR[it.verdict] || VERDICT_COLOR.Unknown}`} data-testid={`pool-analyzer-item-${it.coupon_number}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="font-mono font-bold w-8 shrink-0">#{it.coupon_number}</span>
                        <span className="truncate">{it.found ? `${it.home_team} vs ${it.away_team}` : "— number not in current coupon —"}</span>
                      </div>
                      {it.found && (
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs font-bold">{it.verdict}</span>
                          <span className="text-xs">{it.draw_score}%</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Outcome distribution */}
                {result.outcomes && result.outcomes.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Outcome Probabilities</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {result.outcomes.map(o => (
                        <div key={o.k_draws} className="bg-gray-50 border border-gray-200 rounded-md px-2 py-2 text-center" data-testid={`outcome-${o.k_draws}`}>
                          <p className="text-xs text-gray-500">Exactly {o.k_draws} draws</p>
                          <p className="text-sm font-extrabold text-gray-900">{o.probability_pct}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gbetpools partner CTA */}
                {result.stake_partner && (
                  <a
                    href={result.stake_partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl px-5 py-4 text-center hover:brightness-105 transition-all"
                    data-testid="gbetpools-cta"
                  >
                    <p className="font-extrabold text-base sm:text-lg">Stake on {result.stake_partner.name}</p>
                    <p className="text-xs sm:text-sm text-emerald-50 mt-1">{result.stake_partner.tagline}</p>
                    <span className="inline-flex items-center gap-1 mt-2 text-xs font-bold">
                      Open {result.stake_partner.name} <ExternalLink className="h-3.5 w-3.5" />
                    </span>
                  </a>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default PoolAnalyzerWidget;
